<template>
  <div
    v-if="cards.length"
    class="kbd-runtime"
    :style="gridStyle"
  >
    <Kabbalah_Template_Render
      v-for="(card, index) in cards"
      :key="`${card.key}-${renderSettings.cardPadding}-${renderSettings.spacing}`"
      class="kbd-runtime__card"
      :style="getCardStyle(index)"
      :template-id="templateId"
      :settings="renderSettings"
      :entries="card.entries"
      :max-scale="maxScale"
      :show-placeholders="false"
      @natural-width="(width) => onCardNaturalWidth(card.key, width)"
    />
  </div>
</template>

<script>
/**
 * One screenful of approved Kabbalah records, laid out in the organization's
 * design. Paging is the display's job — this component draws whichever records
 * it is handed, and nothing else.
 */
import Kabbalah_Template_Render from '@/components/kabbalah_N/Kabbalah_Template_Render.vue';
import { getKabbalahCardCapacity_N } from '@/utils/kabbalah_design_render';
import { kabbalahDurationLabel } from '@/utils/kabbalah_duration';
import {
  DEFAULT_KABBALAH_TEMPLATE_ID_N,
  KABBALAH_TEMPLATE_CONTENT_WIDTH,
  buildKabbalahDisplayCardSizes_N,
  defaultKabbalahTemplateSettings_N,
  findKabbalahTemplate_N,
  getKabbalahCardPaddingParts_N,
  normalizeKabbalahCardPadding_N,
  normalizeKabbalahCardSpacing_N,
  normalizeKabbalahDesignSettings_N,
  resolveKabbalahDisplayColumns_N,
} from '@/utils/kabbalah_design_templates';

export default {
  name: 'Kabbalah_Display_Render',
  components: {
    Kabbalah_Template_Render,
  },
  props: {
    // Filtering and ordering belong to the display surface. Preserving that
    // order here makes the record-to-card mapping deterministic.
    records: {
      type: Array,
      default: () => [],
    },
    // One organization-wide row. Null is valid and selects built-in defaults.
    design: {
      type: Object,
      default: null,
    },
    maxScale: {
      type: Number,
      default: 1.35,
    },
    horizontal: {
      type: Boolean,
      default: false,
    },
    verticalScrolling: {
      type: Boolean,
      default: false,
    },
    // Pre-scale artboard width supplied by Display.vue. It lets horizontal
    // cards retain the authored 1/2/3-column width inside a max-content strip.
    viewportWidth: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      // What each rendered card actually measures, keyed by card. Read back only
      // for cards currently on screen, so a page that rotates away can't hold
      // the row open at its width.
      cardWidths: {},
    };
  },
  computed: {
    template() {
      return findKabbalahTemplate_N(
        this.design?.templateId || DEFAULT_KABBALAH_TEMPLATE_ID_N
      );
    },
    // The finder safely resolves stale/unknown stored ids to the first
    // template. Use its real id for settings too so the fallback stays
    // internally consistent.
    templateId() {
      return this.template.id;
    },
    normalizedSettings() {
      return normalizeKabbalahDesignSettings_N(this.design?.settings);
    },
    activeSettings() {
      return this.normalizedSettings[this.templateId]
        || defaultKabbalahTemplateSettings_N(this.templateId);
    },
    renderSettings() {
      return {
        ...this.activeSettings,
        cardPadding: normalizeKabbalahCardPadding_N(this.activeSettings?.cardPadding),
        spacing: normalizeKabbalahCardSpacing_N(this.activeSettings?.spacing),
      };
    },
    // Read from the design as a whole rather than from the active template's
    // settings alone — see `resolveKabbalahDisplayColumns_N`. Display.vue pages
    // the records with this same resolver.
    columns() {
      return resolveKabbalahDisplayColumns_N(this.design);
    },
    authoredCardWidth() {
      const padding = getKabbalahCardPaddingParts_N(this.renderSettings?.cardPadding);
      return KABBALAH_TEMPLATE_CONTENT_WIDTH + padding.horizontal * 2;
    },
    cardSpacing() {
      return normalizeKabbalahCardSpacing_N(this.renderSettings?.spacing);
    },
    // The width a column has to be. Measured from the cards themselves — a
    // design is drawn at its content's width, which is usually far narrower than
    // the authored maximum, and sizing columns to that maximum would leave the
    // difference sitting between the cards as extra gap. The authored width is
    // only the fallback for the first paint, before anything has measured.
    effectiveCardWidth() {
      const measured = this.cards
        .map((card) => Number(this.cardWidths[card.key]) || 0)
        .filter((width) => width > 0);
      return measured.length ? Math.max(...measured) : this.authoredCardWidth;
    },
    // The row is capped at the width its cards occupy and centred, so the space
    // between them is the authored "Between Cards" value and nothing else.
    gridMaxWidth() {
      return (
        this.effectiveCardWidth * this.columns
        + this.cardSpacing * (this.columns - 1)
      );
    },
    entries() {
      return this.records
        .filter(Boolean)
        .map((record, index) => {
          const firstName = String(record.firstName ?? '').trim();
          const lastName = String(record.lastName ?? '').trim();
          const fullName =
            String(record.name ?? '').trim()
            || [firstName, lastName].filter(Boolean).join(' ');

          return {
            // Paired with the slot rather than keyed by id alone: keys only
            // have to be unique within the page, and the pairing keeps them so
            // even if the same record ever reached the list twice.
            key: `${record.id || 'record'}-${index}`,
            fullName,
            firstName,
            lastName,
            gabbai: String(record.gabbai ?? '').trim(),
            kabbalah: String(record.kabbalah ?? '').trim(),
            duration: kabbalahDurationLabel(record.durationType, record.customDays),
            // Retired tokens, always blank — see KabbalahRenderEntry_N.
            city: '',
            beisMedrash: '',
          };
        });
    },
    // A template owns the number of people drawn on one card, and the design's
    // column count owns how many cards stand side by side. The split honours
    // both: cards fill to the template's capacity, but a list too short to reach
    // the chosen column count is spread across the row instead of collapsing
    // into one card. Unlike the editor preview, production never pads with
    // sample rows — every card here holds real records only.
    cards() {
      const entriesPerCard = getKabbalahCardCapacity_N(this.renderSettings?.html || '');
      const sizes = buildKabbalahDisplayCardSizes_N(
        this.entries.length,
        entriesPerCard,
        this.columns
      );

      const cards = [];
      let start = 0;

      sizes.forEach((size, index) => {
        const entries = this.entries.slice(start, start + size);
        start += size;
        cards.push({
          key: `${this.templateId}-${entries[0]?.key || index}`,
          entries,
        });
      });

      return cards;
    },
    // Three columns use six equal sub-tracks and each card spans two. That
    // geometry permits genuinely centred one- and two-card trailing rows without
    // changing a card's width.
    gridStyle() {
      if (this.horizontal) {
        const viewportWidth = Math.max(Number(this.viewportWidth) || 0, 1);
        const gap = this.cardSpacing;
        const cardWidth = Math.min(
          Math.max((viewportWidth - gap * (this.columns - 1)) / this.columns, 1),
          this.effectiveCardWidth
        );
        // What this page's cards actually occupy, against the artboard width
        // Display.vue measured for us. `viewportWidth` is the same box the
        // overflow check compares the rendered track against, so a row that is
        // not wider than it here is not wider than it there either.
        const contentWidth =
          this.cards.length * cardWidth + gap * Math.max(this.cards.length - 1, 0);

        // A row that fits has nothing to scroll. It must also not MEASURE wider
        // than the artboard: the stretch to the authored row width and the gap
        // that spaces one marquee copy from the next both exist only for the
        // loop, and either one would tip the overflow check over and have the
        // row duplicated into a marquee — which would put the same card on
        // screen twice.
        if (contentWidth <= viewportWidth) {
          return {
            display: 'flex',
            width: 'max-content',
            maxWidth: `${viewportWidth}px`,
            gap: `${gap}px`,
            justifyContent: 'center',
            '--kbd-horizontal-card-width': `${cardWidth}px`,
          };
        }

        return {
          display: 'flex',
          width: 'max-content',
          minWidth: `${Math.min(viewportWidth, this.effectiveCardWidth * this.columns + gap * (this.columns - 1))}px`,
          gap: `${gap}px`,
          // The outer marquee duplicates this whole strip. Include the normal
          // card gap at the copy boundary as part of each period.
          paddingInlineEnd: `${gap}px`,
          justifyContent: 'flex-start',
          '--kbd-horizontal-card-width': `${cardWidth}px`,
        };
      }

      const verticalPeriodStyle = this.verticalScrolling
        ? { paddingBottom: `${this.cardSpacing}px` }
        : {};

      if (this.columns === 3) {
        return {
          gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
          columnGap: `${this.cardSpacing}px`,
          rowGap: `${this.cardSpacing}px`,
          maxWidth: `${this.gridMaxWidth}px`,
          marginInline: 'auto',
          ...verticalPeriodStyle,
        };
      }

      return {
        gridTemplateColumns: `repeat(${this.columns}, minmax(0, 1fr))`,
        gap: `${this.cardSpacing}px`,
        maxWidth: `${this.gridMaxWidth}px`,
        marginInline: 'auto',
        ...verticalPeriodStyle,
      };
    },
  },
  methods: {
    onCardNaturalWidth(key, width) {
      const measured = Math.ceil(Number(width) || 0);
      if (measured > 0 && this.cardWidths[key] !== measured) {
        this.cardWidths[key] = measured;
      }
    },
    getCardStyle(index) {
      if (this.horizontal) {
        return {
          flex: '0 0 var(--kbd-horizontal-card-width)',
          width: 'var(--kbd-horizontal-card-width)',
        };
      }

      const total = this.cards.length;
      if (this.columns === 1 || total === 0) return {};

      if (this.columns === 2) {
        const isTrailingSingle = total % 2 === 1 && index === total - 1;
        return isTrailingSingle
          ? {
              gridColumn: '1 / -1',
              justifySelf: 'center',
              width: `calc((100% - ${this.cardSpacing}px) / 2)`,
            }
          : {};
      }

      const remainder = total % 3;
      const completeCount = total - remainder;

      if (index < completeCount) {
        return {
          gridColumn: `${(index % 3) * 2 + 1} / span 2`,
          gridRow: String(Math.floor(index / 3) + 1),
        };
      }

      const lastRow = String(Math.floor(index / 3) + 1);
      if (remainder === 1) {
        return { gridColumn: '3 / span 2', gridRow: lastRow };
      }

      const positionInLastRow = index - completeCount;
      return {
        gridColumn: positionInLastRow === 0 ? '2 / span 2' : '4 / span 2',
        gridRow: lastRow,
      };
    },
  },
};
</script>

<style scoped>
.kbd-runtime {
  display: grid;
  align-items: start;
  width: 100%;
  height: auto;
  white-space: normal;
}

.kbd-runtime__card {
  min-width: 0;
  white-space: normal;
}
</style>
